import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  ShieldAlert,
  Lock,
  Eye,
  EyeOff,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import PageShell from "@/components/PageShell";

// -------------------------------------------------------------------------
// Reset Password page (user-facing, branded, hosted on myspendnova.com).
//
// Reads the reset token from the URL (?token=... or #token=...) which is
// issued by the SpendNova app backend's /api/auth/forgot-password flow,
// then submits a new password to /api/auth/reset-password on that same
// backend. The user never leaves myspendnova.com.
//
// Backend contract (from live OpenAPI spec on 2026-08-08):
//   POST https://bill-ready-1.emergent.host/api/auth/reset-password
//   Body: { "token": <string>, "new_password": <string, 8..72 chars> }
// -------------------------------------------------------------------------

const RESET_ENDPOINT =
  "https://bill-ready-1.emergent.host/api/auth/reset-password";

const MIN_LEN = 8;
const MAX_LEN = 72;

// Extract the reset token from either the query string or the hash
// fragment (some mail clients strip one or the other).
function extractToken() {
  if (typeof window === "undefined") return "";
  try {
    const q = new URLSearchParams(window.location.search || "");
    const t1 = q.get("token");
    if (t1) return t1;
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (hash) {
      const h = new URLSearchParams(hash);
      const t2 = h.get("token");
      if (t2) return t2;
    }
  } catch (_) {}
  return "";
}

const ResetPasswordRedirect = () => {
  const [token, setToken] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    setToken(extractToken());
  }, []);

  const validation = useMemo(() => {
    if (!pw) return "";
    if (pw.length < MIN_LEN) return `Password must be at least ${MIN_LEN} characters.`;
    if (pw.length > MAX_LEN) return `Password must be ${MAX_LEN} characters or fewer.`;
    if (pw2 && pw !== pw2) return "Passwords don't match.";
    return "";
  }, [pw, pw2]);

  const canSubmit =
    Boolean(token) &&
    pw.length >= MIN_LEN &&
    pw.length <= MAX_LEN &&
    pw === pw2 &&
    !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!token) {
      setErrorMsg(
        "Your reset link is missing its token. Request a new password-reset email and try again."
      );
      return;
    }
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await fetch(RESET_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ token, new_password: pw }),
      });
      if (res.ok) {
        setDone(true);
      } else {
        let msg = "";
        try {
          const j = await res.json();
          if (typeof j?.detail === "string") msg = j.detail;
          else if (Array.isArray(j?.detail) && j.detail[0]?.msg) msg = j.detail[0].msg;
        } catch (_) {}
        if (!msg) {
          if (res.status === 400 || res.status === 422)
            msg = "This reset link is invalid or has already been used.";
          else if (res.status === 410)
            msg = "This reset link has expired. Please request a new one.";
          else msg = "Something went wrong resetting your password. Please try again.";
        }
        setErrorMsg(msg);
      }
    } catch (_) {
      setErrorMsg(
        "We couldn't reach the SpendNova reset service. Check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell navyTheme={true}>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(1000px 500px at 50% -100px, rgba(34,197,133,0.18) 0%, rgba(34,197,133,0.00) 60%)",
        }}
      >
        <div className="mx-auto max-w-md px-4 md:px-6 py-14 md:py-20">
          <div
            className="rounded-3xl p-7 md:p-8"
            style={{
              background:
                "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
              border: "1px solid rgba(255,255,255,0.12)",
              boxShadow:
                "0 40px 80px -40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
            data-testid="reset-password-card"
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.22em]"
              style={{
                background: "rgba(34,197,133,0.12)",
                border: "1px solid rgba(34,197,133,0.45)",
                color: "#22C585",
              }}
              data-testid="reset-password-badge"
            >
              <Lock size={12} />
              Password Reset
            </div>

            {done ? (
              <>
                <div className="mt-5 flex items-center gap-3">
                  <CheckCircle2 size={28} className="text-[#22C585] shrink-0" />
                  <h1
                    className="text-2xl md:text-3xl font-extrabold text-white leading-tight tracking-tight"
                    style={{ fontFamily: "Sora, Inter, sans-serif" }}
                    data-testid="reset-password-success-title"
                  >
                    Password updated.
                  </h1>
                </div>
                <p
                  className="mt-4 text-[14px] text-[#C7D0DF] leading-relaxed"
                  data-testid="reset-password-success-body"
                >
                  Your SpendNova password has been changed. Open the SpendNova
                  app and sign in with your new password.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/install"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-[14px] font-semibold text-white transition-transform hover:-translate-y-[1px]"
                    style={{
                      background: "#22C585",
                      boxShadow:
                        "0 10px 30px -12px rgba(34,197,133,0.55), inset 0 1px 0 rgba(255,255,255,0.20)",
                    }}
                    data-testid="reset-password-open-app"
                  >
                    Open SpendNova
                    <ArrowRight size={16} />
                  </Link>
                  <Link
                    to="/support"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-[14px] font-semibold text-white transition-transform hover:-translate-y-[1px]"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.22)",
                    }}
                    data-testid="reset-password-support"
                  >
                    Need help?
                  </Link>
                </div>
              </>
            ) : (
              <>
                <h1
                  className="mt-5 text-2xl md:text-3xl font-extrabold text-white leading-tight tracking-tight"
                  style={{ fontFamily: "Sora, Inter, sans-serif" }}
                  data-testid="reset-password-title"
                >
                  Set a new password
                </h1>
                <p
                  className="mt-3 text-[14px] text-[#C7D0DF] leading-relaxed"
                  data-testid="reset-password-subtitle"
                >
                  Choose a strong password between {MIN_LEN} and {MAX_LEN}{" "}
                  characters. You&apos;ll use it the next time you sign in to
                  SpendNova.
                </p>

                {!token ? (
                  <div
                    className="mt-5 rounded-xl p-3 flex items-start gap-2.5"
                    style={{
                      background: "rgba(248,183,14,0.08)",
                      border: "1px dashed rgba(248,183,14,0.45)",
                    }}
                    data-testid="reset-password-missing-token"
                  >
                    <ShieldAlert size={15} className="text-[#F8B70E] shrink-0 mt-0.5" />
                    <p className="text-[12px] text-[#F0E5C6] leading-relaxed">
                      <span className="text-white font-semibold">
                        Reset link incomplete.
                      </span>{" "}
                      This page needs a token from your reset email. Please
                      open the newest reset email on the same device and tap
                      the link again.
                    </p>
                  </div>
                ) : null}

                <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="reset-password-form">
                  <div>
                    <label
                      htmlFor="new-password"
                      className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-bold"
                    >
                      New password
                    </label>
                    <div className="mt-1.5 relative">
                      <input
                        id="new-password"
                        type={showPw ? "text" : "password"}
                        autoComplete="new-password"
                        value={pw}
                        onChange={(e) => setPw(e.target.value)}
                        maxLength={MAX_LEN}
                        placeholder="At least 8 characters"
                        className="w-full rounded-xl px-3.5 py-3 pr-11 text-[14px] text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[#22C585]/50"
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.14)",
                        }}
                        data-testid="reset-password-input-new"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                        aria-label={showPw ? "Hide password" : "Show password"}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/5"
                        data-testid="reset-password-toggle-visibility"
                      >
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="confirm-password"
                      className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-bold"
                    >
                      Confirm new password
                    </label>
                    <div className="mt-1.5">
                      <input
                        id="confirm-password"
                        type={showPw ? "text" : "password"}
                        autoComplete="new-password"
                        value={pw2}
                        onChange={(e) => setPw2(e.target.value)}
                        maxLength={MAX_LEN}
                        placeholder="Re-enter the password"
                        className="w-full rounded-xl px-3.5 py-3 text-[14px] text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[#22C585]/50"
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.14)",
                        }}
                        data-testid="reset-password-input-confirm"
                      />
                    </div>
                  </div>

                  {validation ? (
                    <p
                      className="text-[12px] text-[#F8B70E]"
                      data-testid="reset-password-validation"
                    >
                      {validation}
                    </p>
                  ) : null}

                  {errorMsg ? (
                    <div
                      className="rounded-xl p-3 flex items-start gap-2.5"
                      style={{
                        background: "rgba(230,57,70,0.10)",
                        border: "1px solid rgba(230,57,70,0.45)",
                      }}
                      data-testid="reset-password-error"
                    >
                      <ShieldAlert size={15} className="text-[#E63946] shrink-0 mt-0.5" />
                      <p className="text-[12.5px] text-[#F5D0D3] leading-relaxed">
                        {errorMsg}
                      </p>
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-[14px] font-semibold text-white transition-transform hover:-translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    style={{
                      background: "#22C585",
                      boxShadow:
                        "0 10px 30px -12px rgba(34,197,133,0.55), inset 0 1px 0 rgba(255,255,255,0.20)",
                    }}
                    data-testid="reset-password-submit"
                  >
                    {submitting ? "Updating password…" : "Update password"}
                  </button>
                </form>

                <div
                  className="mt-5 rounded-xl p-3 flex items-start gap-2.5"
                  style={{
                    background: "rgba(34,197,133,0.06)",
                    border: "1px dashed rgba(34,197,133,0.35)",
                  }}
                >
                  <ShieldCheck size={15} className="text-[#22C585] shrink-0 mt-0.5" />
                  <p className="text-[12px] text-[#C7D0DF] leading-relaxed">
                    Your new password is sent over TLS 1.3 and hashed by
                    SpendNova&apos;s auth service before storage. We never see
                    or store your plain-text password.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default ResetPasswordRedirect;
